import { PropsWithChildren } from "react"
import {
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  ListBoxProps,
  Popover,
  PopoverProps,
  Button as RACButton,
  Select as RACSelect,
  SelectValue as RACSelectValue,
  SelectProps,
  SelectValueProps,
} from "react-aria-components"
import { RefCallBack } from "react-hook-form"

import Icon from "@/globalComponents/atoms/Icon"
import { cn } from "@/utilities/etc"

import styles from "./Select.module.css"

type Props<T extends object> = Omit<
  SelectProps<T>,
  "onSelectionChange" | "selectedKey" | "isDisabled"
> & {
  value?: any
  onChange?: (value: any) => void
  disabled?: boolean
  name?: string
  onBlur?: () => void
}

const Select = <T extends object>(props: PropsWithChildren<Props<T>>) => {
  const { value, onChange, disabled, onBlur, name, ...restProps } = props
  return (
    <RACSelect
      {...restProps}
      className={cn("field", props.className, styles.select)}
      onSelectionChange={onChange}
      selectedKey={value}
      isDisabled={disabled}
      name={name}
      onBlur={onBlur}
    >
      {props.children}
    </RACSelect>
  )
}

Select.Value = <T extends object>(
  props: SelectValueProps<T> & { innerRef?: RefCallBack }
) => {
  const innerRef = props.innerRef
  return (
    <RACButton
      className={cn(props.className, "field--input", "input--1")}
      ref={innerRef}
    >
      <RACSelectValue {...props} />
      <Icon name="chevronDown" />
    </RACButton>
  )
}

Select.Popover = (props: PopoverProps) => {
  return (
    <Popover
      {...props}
      className={cn("field--popover", props.className, styles.popover)}
    >
      {props.children}
    </Popover>
  )
}

Select.ListBox = <T extends object>(props: ListBoxProps<T>) => {
  return (
    <ListBox
      {...props}
      className={cn("listBox", props.className, styles.listBox)}
    >
      {props.children}
    </ListBox>
  )
}

Select.ListBoxItem = (props: ListBoxItemProps & { textValue?: string }) => {
  return (
    <ListBoxItem
      {...props}
      className={cn("listBoxItem", props.className, styles.listBoxItem)}
      textValue={props.textValue}
    >
      {props.children}
    </ListBoxItem>
  )
}

export default Select
